import { useEffect, useState } from "react";
import { Target, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import axios, { API } from "@/lib/apiClient";
import { toast } from "sonner";

export const CompetitorTracker = ({ auditId, brandName }) => {
  const [tracked, setTracked] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    axios
      .get(`${API}/competitors/mine`, { params: { brand_name: brandName } })
      .then((res) => setTracked(res.data))
      .catch(() => setTracked([]));
  };

  useEffect(load, [brandName]);

  const track = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/competitors/track`, { audit_id: auditId, competitor_name: name.trim() });
      toast.success(`Now tracking ${name.trim()} - the Sovereign Defense scanner will run periodic mini-audits on them.`);
      setName("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not track competitor.");
    } finally {
      setLoading(false);
    }
  };

  const untrack = async (id) => {
    try {
      await axios.delete(`${API}/competitors/${id}`);
      setTracked((prev) => prev.filter((c) => c.id !== id));
    } catch {
      toast.error("Could not remove tracked competitor.");
    }
  };

  return (
    <div className="rounded-lg border border-warning-amber/30 bg-warning-amber/5 p-5" data-testid="competitor-tracker">
      <h3 className="mb-1 flex items-center gap-2 font-heading text-lg font-bold text-white">
        <Target className="h-4 w-4 text-warning-amber" /> Track Competitors
      </h3>
      <p className="mb-4 text-xs text-slate-400">
        Name up to 5 competitors for continuous mini-audits - real comparative score history powers Competitor Hijack Alerts.
      </p>
      <div className="mb-4 flex gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Competitor brand name"
          data-testid="competitor-name-input"
        />
        <Button onClick={track} disabled={loading || tracked.length >= 5} data-testid="track-competitor-btn">
          Track
        </Button>
      </div>
      {tracked.length === 0 ? (
        <p className="text-xs text-slate-500" data-testid="no-tracked-competitors">No competitors tracked yet.</p>
      ) : (
        <ul className="space-y-2" data-testid="tracked-competitors-list">
          {tracked.map((c) => (
            <li key={c.id} className="flex items-center justify-between rounded-md border border-surface-border bg-obsidian px-3 py-2 text-sm text-slate-300" data-testid={`tracked-competitor-${c.competitor_name}`}>
              <span>{c.competitor_name}</span>
              <button onClick={() => untrack(c.id)} className="text-slate-500 hover:text-sovereign" data-testid={`untrack-competitor-${c.competitor_name}`}>
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
